import { Text } from "@shared/components/text";
import { Head } from "@shared/components/head";
import Button from "@shared/components/md-design-system/Button/Button";
import { View, ScrollView } from "react-native";
import { ActivityName } from "../../types";
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { useForm } from "@shared/hooks";
import { Input } from "@shared/components/md-design-system/Input";
import { MEASUREMENT_FORM_INPUTS } from "../../constants/measurementForm";
import { usePost } from "app/queryProvider";
import { TMeasurementResponse, IPathwayMeasurementRequest } from "app/_modules/pathways/entities";
import { Loading } from "@shared/components/loading";
import { useAuth } from "app/auth/hooks/useAuth";

export const MeasurementForm = ({
    activityName,
    onSubmitStart,
    onSubmitSuccess,
    onSubmitError
}: {
    activityName: ActivityName;
    onSubmitStart: () => void;
    onSubmitSuccess: (result: TMeasurementResponse) => void;
    onSubmitError: () => void;
}) => {
    const { inputs } = MEASUREMENT_FORM_INPUTS[activityName as Exclude<ActivityName, ActivityName.ECG | ActivityName.SPIROMETRY>];
    const { t } = dynamicTranslations();
    const { meData } = useAuth()


    const { mutate: createMeasurement, isPending: isCreatingMeasurement } = usePost<IPathwayMeasurementRequest, TMeasurementResponse>({
        options: {
            onSuccess: (result) => {
                onSubmitSuccess(result);
            },
            onError: () => {
                // The POST failed after we already flipped the wizard into the
                // "waiting for measurement" websocket state (onSubmitStart is
                // called before the request fires). Without this, a failed
                // manual-measurement submission leaves the user stuck on the
                // waiting screen until the 30s websocket timeout fires.
                onSubmitError();
            },
        }
    });

    const telemonitoringPathwayId = meData?.current_pathway.id || "";
    const type = activityName

    const { form, Form, formFields, submitForm } = useForm({
        inputs,
        onSubmit: async (values) => {
            const payload = {
                telemonitoringPathwayId,
                type,
                timestamp: new Date().toISOString(),
                measurement: {
                    ...values
                }
            }
            onSubmitStart();

            createMeasurement({
                resource: { name: "pathways", id: telemonitoringPathwayId },
                url: "/manual-measurement",
                data: payload,
            });
        },
    });

    if (isCreatingMeasurement) {
        return <Loading message={t("commons.loading.sendingMeasurement")} />
    }

    return (
        <ScrollView className="flex-1" contentContainerStyle={{ flexGrow: 1 }} keyboardShouldPersistTaps="handled">
            <View className="flex-col gap-6">
                <View className="flex-col gap-2">
                    <Head type="h2">{t("measurement.manualEntryTitle")}</Head>
                    <Text className="text-base text-gray-500">{t("measurement.manualEntrySubtitle")}</Text>
                </View>
                <Form form={form} styles={{ Body: { borderTopWidth: 0 } }}>
                    <View className='flex-col gap-4 bg-white'>
                        {formFields.map((field) => (
                            <View key={field.name} className='flex-col gap-2 bg-white'>
                                <Form.Item
                                    name={field.name}
                                    rules={field.rules}
                                    noStyle
                                >
                                    <Input
                                        {...field}
                                        label={field.unit ? `${t(field.label!)} (${field.unit})` : field.label}
                                        unit={undefined}
                                        size="large"
                                    />
                                </Form.Item>
                            </View>
                        ))}
                    </View>
                </Form>
                <Button size="large" variant="primary" onPress={submitForm} disabled={isCreatingMeasurement}>
                    {t("buttons.save")}
                </Button>
            </View>
        </ScrollView>
    );
};
